import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Skeleton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ImageCard = ({ image, onEdit, onPreview, onDelete }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card 
        sx={{ 
          height: '100%',
          cursor: isDragging ? 'grabbing' : 'grab',
          position: 'relative',
          '&:hover .drag-handle': {
            opacity: 1
          }
        }}
      >
        <Box
          className="drag-handle"
          {...attributes}
          {...listeners}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 1,
            backgroundColor: 'rgba(0,0,0,0.6)',
            borderRadius: 1,
            opacity: 0,
            transition: 'opacity 0.2s',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing'
            }
          }}
        >
          <IconButton size="small" sx={{ color: 'white' }}>
            <DragIndicatorIcon />
          </IconButton>
        </Box>

        <Box position="relative" paddingTop="100%">
          {imageLoading && (
            <Skeleton
              variant="rectangular"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
            />
          )}
          <CardMedia
            component="img"
            image={imageError ? '/placeholder-image.png' : image.image_url}
            alt={`Product ${image.sku}`}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageLoading(false);
              setImageError(true);
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: imageLoading ? 'none' : 'block'
            }}
          />
        </Box>

        <CardContent>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            SKU: {image.sku}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={`Order: ${image.sort_order}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between' }}>
          <IconButton
            size="small"
            onClick={() => onPreview(image)}
            title="Preview"
            color="primary"
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onEdit(image)}
            title="Edit"
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(image)}
            title="Delete"
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </CardActions>
      </Card>
    </div>
  );
};

const ImageGallery = ({ images, onEdit, onPreview, onDragEnd, onRefresh }) => {
  const [items, setItems] = useState(images);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  React.useEffect(() => {
    setItems(images);
  }, [images]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      
      const newItems = arrayMove(items, oldIndex, newIndex);
      
      // Update sort_order values
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        sort_order: index
      }));
      
      setItems(updatedItems);
      onDragEnd(updatedItems);
    }
  };

  const handleDeleteClick = (image) => {
    setSelectedImage(image);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedImage) return;

    setDeleteLoading(true);

    try {
      const response = await fetch('https://elnaf.fi/new/api/delete_product_image.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: selectedImage.id })
      });

      const data = await response.json();
      
      if (response.ok && (data.message?.includes('uurla') || data.message?.includes('silindi'))) {
        setDeleteDialogOpen(false);
        setSelectedImage(null);
        await onRefresh();
      } else {
        alert(data.message || 'Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map(item => item.id)}
          strategy={rectSortingStrategy}
        >
          <Grid container spacing={2}>
            {items.map((image) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                <ImageCard
                  image={image}
                  onEdit={onEdit}
                  onPreview={onPreview}
                  onDelete={handleDeleteClick}
                />
              </Grid>
            ))}
          </Grid>
        </SortableContext>
      </DndContext>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this image for SKU: <strong>{selectedImage?.sku}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={20} color="inherit" />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImageGallery;